/** Loading, empty and error states. Same contract as the POS's, MUI markup. */

import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export function TableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <Stack spacing={1} aria-hidden>
      <Skeleton variant="rectangular" height={44} />
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} variant="rectangular" height={36} />
      ))}
    </Stack>
  );
}

export function EmptyState({
  title,
  body,
  action,
  compact = false,
}: {
  title: string;
  /**
   * What to do about it, and — where a filter is involved — **which filter returned nothing**.
   * "No sales between 31 Aug and 13 Sep at any shop" is an answer a person can act on; "No rows
   * for this filter" makes them go and check the filter they can already see.
   */
  body?: string;
  action?: ReactNode;
  /** Inside a card or a grid overlay, where the 64px of air would push the panel off-screen. */
  compact?: boolean;
}) {
  return (
    <Box sx={{ py: compact ? 4 : 8, px: 3, textAlign: 'center' }}>
      <Typography sx={{ fontWeight: 700, fontSize: compact ? 15 : 18 }}>{title}</Typography>
      {body !== undefined && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mx: 'auto', maxWidth: '42ch' }}>
          {body}
        </Typography>
      )}
      {action !== undefined && <Box sx={{ mt: 3 }}>{action}</Box>}
    </Box>
  );
}

export function ErrorState({
  title,
  body,
  onRetry,
}: {
  /** What happened, in the user's terms. */
  title: string;
  /** What to do next. Always present — never "Something went wrong". */
  body: string;
  onRetry?: () => void;
}) {
  return (
    <Alert
      severity="error"
      action={
        onRetry !== undefined ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{title}</AlertTitle>
      {body}
    </Alert>
  );
}
